import React, { useEffect, useRef, useState } from 'react'
import { useMutation, type UseMutationResult } from '@tanstack/react-query'
import { additionalInfoSessionPath } from 'components/routes/api'
import { api } from 'components/api'

interface OrganizationOption {
  id: number
  name: string
}

interface Props {
  organizations: OrganizationOption[]
}

type RedirectPath = string | null

interface AdditionalInfoResponse {
  status: string
  redirectPath: RedirectPath
}

interface SubmitParams {
  name: string
  organizationId: string
}

async function submitAdditionalInfo (params: SubmitParams): Promise<AdditionalInfoResponse> {
  const res = await api.postWithCsrf({
    url: additionalInfoSessionPath(),
    data: {
      name: params.name,
      organization_id: params.organizationId
    }
  })

  return res.data as AdditionalInfoResponse
}

function AdditionalInfoForm ({ organizations }: Props): React.ReactElement {
  const nameInputRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState('')
  const [organizationId, setOrganizationId] = useState('')
  const [redirectPath, setRedirectPath] = useState<RedirectPath>(null)
  const [error, setError] = useState<boolean>(false)

  const defaultErrorMessage = "We're sorry there was an error"

  useEffect(() => {
    nameInputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (redirectPath == null) { return }
    const timeout = setTimeout(() => {
      window.location.href = redirectPath
    }, 1000)

    return () => clearTimeout(timeout)
  }, [redirectPath])

  const mutation: UseMutationResult<AdditionalInfoResponse, unknown, SubmitParams> = useMutation({
    mutationFn: submitAdditionalInfo,
    onSuccess: (data) => {
      setError(false)
      setRedirectPath(data.redirectPath)
    },
    onError: () => {
      setError(true)
    }
  })

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    mutation.mutate({ name: name.trim(), organizationId })
  }

  if (redirectPath != null) {
    return <p>Thanks! You will be forwarded to the review form.</p>
  }

  if (error) {
    return <p>{defaultErrorMessage}</p>
  }

  const isSubmitting = mutation.isPending
  const canSubmit = name.trim().length > 0 && organizationId.length > 0 && !isSubmitting

  return (
    <div className='additional-info-modal email-only-modal focusable'>
      <form id='auth-additional-info-form' onSubmit={handleSubmit}>
        <div className='header-container'>
          <i className='fas fa-building-columns header-icon' aria-hidden='true' />
          <div className='header pro-head-4' role='heading'>
            Additional Information
          </div>
        </div>

        <p className='pro-text-xs required-fields-note'>
          Fields marked with an asterisk<span className='required-asterisk'>*</span> are required.
        </p>

        <label htmlFor='full-name'>
          Full Name<span className='required-asterisk'>*</span>
        </label>
        <input
          ref={nameInputRef}
          id='full-name'
          aria-label='full name'
          name='name'
          type='text'
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isSubmitting}
        />

        <label htmlFor='organization-id'>
          Organization<span className='required-asterisk'>*</span>
        </label>
        <select
          id='organization-id'
          aria-label='organization'
          name='organization_id'
          value={organizationId}
          onChange={(e) => setOrganizationId(e.target.value)}
          disabled={isSubmitting}
        >
          <option value=''>Select an organization</option>
          {organizations.map((o) => (
            <option key={o.id} value={String(o.id)}>{o.name}</option>
          ))}
        </select>

        <button
          className='button float-right next pro-closest-shadow'
          type='submit'
          disabled={!canSubmit}
        >
          {isSubmitting ? 'Submitting...' : 'Continue'}
        </button>

        <div className='clearfix' />
      </form>
    </div>
  )
}

export function AdditionalInfoModal (props: Props): React.ReactElement {
  return (
    <div className='pro-modal-overlay'>
      <div className='pro-modal'>
        <AdditionalInfoForm {...props} />
      </div>
    </div>
  )
}
